import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpenCheck,
  CalendarPlus,
  ClipboardList,
  Copy,
  GraduationCap,
  Edit3,
  Loader2,
  Plus,
  Save,
  Trash2,
  UserRound,
} from "lucide-react";
import { CalidadControlDocumentalPanel } from "../../../components/control-modificaciones/CalidadControlDocumentalPanel";
import { Iso900114001ConfirmDialog } from "../../../components/shared/Iso900114001ConfirmDialog";
import {
  Iso900114001CrudTable,
  type Iso900114001TableColumn,
} from "../../../components/shared/Iso900114001CrudTable";
import { Iso900114001Filters } from "../../../components/shared/Iso900114001Filters";
import { Iso900114001IconButton } from "../../../components/shared/Iso900114001IconButton";
import { Iso900114001MasterDetailTable } from "../../../components/shared/Iso900114001MasterDetailTable";
import {
  Iso900114001Checkbox,
  Iso900114001Input,
  Iso900114001Select,
  Iso900114001Textarea,
} from "../../../components/shared/Iso900114001Fields";
import { Iso900114001Modal } from "../../../components/shared/Iso900114001Modal";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001ScrollableCell } from "../../../components/shared/Iso900114001ScrollableCell";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../components/shared/Iso900114001Tabs";
import { getCertificationRequestError } from "../../../components/shared/request";
import { isRequestCancellation } from "@shared/api/client";
import { useIso900114001DebouncedValue } from "../../../components/shared/useIso900114001FilteredRequest";
import {
  createFormacionEvaluationYear,
  createFormacionYear,
  deleteCourse,
  deleteEmployee,
  deleteEvaluation,
  deleteFormacionYear,
  formacionControlEndpoint,
  getFormacion,
  saveCourse,
  saveEmployee,
  saveEvaluation,
} from "../api/formacion";
import {
  emptyCourse,
  emptyEmployee,
  emptyEvaluation,
  type CoursePayload,
  type EmployeePayload,
  type EvaluationCopyField,
  type EvaluationPayload,
  type FormacionCurso,
  type FormacionEmpleado,
  type FormacionEvaluacion,
  type FormacionResponse,
} from "../types/formacion";

type Props = { onBack: () => void };
type Tab = "formacion" | "evaluacion" | "control";
type DeleteTarget = {
  kind: "employee" | "course" | "evaluation" | "year";
  id: number;
  label: string;
};

const evaluationCopyOptions: Array<{ id: EvaluationCopyField; label: string; required?: boolean }> = [
  { id: "tipo", label: "Tipo" },
  { id: "tema", label: "Tema", required: true },
  { id: "fecha_referencia", label: "Fecha de referencia" },
  { id: "impartido_por", label: "Impartido por" },
  { id: "asistentes", label: "Asistentes" },
  { id: "evaluacion_eficacia", label: "Evaluación de eficacia" },
  { id: "observaciones", label: "Observaciones" },
];

export function CalidadFormacionSection({ onBack }: Props) {
  const [tab, setTab] = useState<Tab>("formacion");
  const [data, setData] = useState<FormacionResponse | null>(null);
  const [filters, setFilters] = useState({ anio: "", search: "" });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [employeeForm, setEmployeeForm] = useState<{
    open: boolean;
    id: number | null;
    value: EmployeePayload;
  }>({ open: false, id: null, value: { ...emptyEmployee } });
  const [courseForm, setCourseForm] = useState<{
    open: boolean;
    id: number | null;
    value: CoursePayload;
  }>({ open: false, id: null, value: emptyCourse(0, 0) });
  const [evaluationForm, setEvaluationForm] = useState<{
    open: boolean;
    id: number | null;
    value: EvaluationPayload;
  }>({ open: false, id: null, value: emptyEvaluation(0) });
  const [yearForm, setYearForm] = useState({ open: false, year: 2026 });
  const [evaluationYearForm, setEvaluationYearForm] = useState<{ open: boolean; source: number; target: number; fields: EvaluationCopyField[] }>({
    open: false,
    source: 2025,
    target: 2026,
    fields: evaluationCopyOptions.map((item) => item.id),
  });
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const debouncedSearch = useIso900114001DebouncedValue(filters.search.trim(), 300);
  const requestSequenceRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const load = useCallback(async (_force = false) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const sequence = ++requestSequenceRef.current;

    setLoading(true);
    setError(null);

    try {
      const response = await getFormacion({
        anio: filters.anio || undefined,
        search: debouncedSearch || undefined,
      }, controller.signal);

      if (sequence !== requestSequenceRef.current) {
        return;
      }

      setData(response);

      if (filters.anio === "" && response.meta.selected_year !== null) {
        setFilters((current) => ({
          ...current,
          anio: String(response.meta.selected_year),
        }));
      }

      setSelectedEmployeeId((current) => (
        current !== null && response.data.empleados.some((employee) => employee.id === current)
          ? current
          : null
      ));
    } catch (requestError) {
      if (isRequestCancellation(requestError) || sequence !== requestSequenceRef.current) {
        return;
      }

      setError(getCertificationRequestError(requestError, "No se pudo cargar la formación."));
    } finally {
      if (sequence === requestSequenceRef.current) {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
        setLoading(false);
      }
    }
  }, [debouncedSearch, filters.anio]);

  useEffect(() => {
    void load(false);

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [load]);

  const employees = data?.data.empleados ?? [];
  const courses = data?.data.cursos ?? [];
  const evaluations = data?.data.evaluaciones ?? [];
  const controls = data?.data.control_modificaciones ?? [];
  const selectedEmployee = useMemo(
    () => employees.find((employee) => employee.id === selectedEmployeeId) ?? null,
    [employees, selectedEmployeeId],
  );
  const visibleCourses = useMemo(
    () => selectedEmployeeId === null
      ? courses
      : courses.filter((course) => course.empleado_id === selectedEmployeeId),
    [courses, selectedEmployeeId],
  );
  const courseCountByEmployee = useMemo(() => {
    const counts = new Map<number, number>();
    for (const course of courses) {
      counts.set(course.empleado_id, (counts.get(course.empleado_id) ?? 0) + 1);
    }
    return counts;
  }, [courses]);

  const selectEmployee = (employeeId: number | null) => {
    setSelectedEmployeeId(employeeId);
  };

  const openEmployee = (row?: FormacionEmpleado) => {
    setFormError(null);
    setEmployeeForm({
      open: true,
      id: row?.id ?? null,
      value: row
        ? {
            nombre: row.nombre,
            iniciales: row.iniciales,
            empresa: row.empresa,
            antiguedad_referencia: row.antiguedad_referencia,
            formacion_si_referencia: row.formacion_si_referencia,
            encuesta_emergencias_referencia: row.encuesta_emergencias_referencia,
            prl_referencia: row.prl_referencia,
          }
        : { ...emptyEmployee },
    });
  };

  const openCourse = (row?: FormacionCurso) => {
    if (!data?.meta.selected_year_id || (!row && !selectedEmployee)) {
      return;
    }
    setFormError(null);
    setCourseForm({
      open: true,
      id: row?.id ?? null,
      value: row
        ? {
            formacion_anio_id: row.formacion_anio_id,
            empleado_id: row.empleado_id,
            fecha: row.fecha,
            fecha_referencia: row.fecha_referencia,
            nombre: row.nombre,
            categoria: row.categoria,
          }
        : emptyCourse(data.meta.selected_year_id, selectedEmployee?.id ?? 0),
    });
  };

  const openEvaluation = (row?: FormacionEvaluacion) => {
    if (!data?.meta.selected_year_id) {
      return;
    }
    setFormError(null);
    setEvaluationForm({
      open: true,
      id: row?.id ?? null,
      value: row
        ? {
            formacion_anio_id: row.formacion_anio_id,
            tipo: row.tipo,
            tema: row.tema,
            fecha: row.fecha,
            fecha_referencia: row.fecha_referencia,
            impartido_por: row.impartido_por,
            asistentes: row.asistentes,
            evaluacion_eficacia: row.evaluacion_eficacia,
            observaciones: row.observaciones,
          }
        : emptyEvaluation(data.meta.selected_year_id),
    });
  };

  const saveEmployeeForm = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await saveEmployee(employeeForm.id, employeeForm.value);
      setEmployeeForm({ open: false, id: null, value: { ...emptyEmployee } });
      await load(true);
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudo guardar el empleado."));
    } finally {
      setSubmitting(false);
    }
  };

  const saveCourseForm = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await saveCourse(courseForm.id, courseForm.value);
      setCourseForm((current) => ({ ...current, open: false }));
      await load(true);
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudo guardar el curso."));
    } finally {
      setSubmitting(false);
    }
  };

  const saveEvaluationForm = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await saveEvaluation(evaluationForm.id, evaluationForm.value);
      setEvaluationForm((current) => ({ ...current, open: false }));
      await load(true);
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudo guardar la evaluación."));
    } finally {
      setSubmitting(false);
    }
  };

  const saveYear = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await createFormacionYear(yearForm.year);
      setYearForm((current) => ({ ...current, open: false }));
      setFilters((current) => ({ ...current, anio: String(yearForm.year) }));
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudo crear el año."));
    } finally {
      setSubmitting(false);
    }
  };

  const saveEvaluationYear = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      await createFormacionEvaluationYear(
        evaluationYearForm.source,
        evaluationYearForm.target,
        evaluationYearForm.fields,
      );
      setEvaluationYearForm((current) => ({ ...current, open: false }));
      setFilters((current) => ({ ...current, anio: String(evaluationYearForm.target) }));
    } catch (requestError) {
      setFormError(getCertificationRequestError(requestError, "No se pudo crear la plantilla anual de evaluación."));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) {
      return;
    }
    setSubmitting(true);
    try {
      if (deleteTarget.kind === "employee") {
        await deleteEmployee(deleteTarget.id);
      } else if (deleteTarget.kind === "course") {
        await deleteCourse(deleteTarget.id);
      } else if (deleteTarget.kind === "evaluation") {
        await deleteEvaluation(deleteTarget.id);
      } else {
        await deleteFormacionYear(deleteTarget.id);
      }

      const removedKind = deleteTarget.kind;
      setDeleteTarget(null);
      if (removedKind === "year") {
        setFilters((current) => ({ ...current, anio: "" }));
      } else if (removedKind === "employee") {
        setSelectedEmployeeId(null);
        await load(true);
      } else {
        await load(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const evaluationColumns: Iso900114001TableColumn<FormacionEvaluacion>[] = [
    {
      key: "tipo",
      header: "Tipo",
      className: "iso9001-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.tipo ?? undefined}>
          {row.tipo || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
    {
      key: "tema",
      header: "Tema",
      className: "iso9001-extra-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.tema}>
          {row.tema}
        </Iso900114001ScrollableCell>
      ),
    },
    {
      key: "fecha",
      header: "Fecha",
      render: (row) => row.fecha || row.fecha_referencia || "—",
    },
    {
      key: "impartido",
      header: "Impartido por",
      className: "iso9001-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.impartido_por ?? undefined}>
          {row.impartido_por || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
    {
      key: "asistentes",
      header: "Asistentes",
      className: "iso9001-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.asistentes ?? undefined}>
          {row.asistentes || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
    {
      key: "eficacia",
      header: "Evaluación eficacia",
      className: "iso9001-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.evaluacion_eficacia ?? undefined}>
          {row.evaluacion_eficacia || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
    {
      key: "observaciones",
      header: "Observaciones",
      className: "iso9001-extra-long-column",
      render: (row) => (
        <Iso900114001ScrollableCell title={row.observaciones ?? undefined}>
          {row.observaciones || "—"}
        </Iso900114001ScrollableCell>
      ),
    },
  ];

  return (
    <section className="iso9001-section-stack">
      <Iso900114001SectionHeader
        eyebrow="Calidad y medioambiente"
        title="Formación"
        description="Formación por empleado, organizada por año, con evaluación de la eficacia y empleados históricos independientes de los usuarios de la aplicación."
        onBack={onBack}
        onRefresh={() => void load(true)}
        refreshing={loading}
      />

      <div className="iso9001-overview-grid">
        <Iso900114001OverviewCard icon={UserRound} label="Empleados" value={data?.meta.empleados_total ?? 0} />
        <Iso900114001OverviewCard icon={GraduationCap} label="Cursos del año" value={data?.meta.cursos_total ?? 0} />
        <Iso900114001OverviewCard icon={BookOpenCheck} label="Evaluaciones" value={data?.meta.evaluaciones_total ?? 0} />
        <Iso900114001OverviewCard icon={ClipboardList} label="Control documental" value={controls.length} />
      </div>

      <Iso900114001Tabs
        tabs={[
          { id: "formacion", label: "Formación", count: courses.length },
          { id: "evaluacion", label: "Evaluación", count: evaluations.length },
          { id: "control", label: "Control documental", count: controls.length },
        ]}
        activeTab={tab}
        onChange={setTab}
        ariaLabel="Formación"
      />

      {tab === "control" ? (
        <CalidadControlDocumentalPanel
          rows={controls}
          loading={loading}
          error={error}
          baseEndpoint={formacionControlEndpoint}
          onReload={() => load(true)}
        />
      ) : (
        <>
          <Iso900114001Filters
            title="Seleccionar formación"
            count={tab === "formacion" ? visibleCourses.length : evaluations.length}
            onReset={() => {
              setSelectedEmployeeId(null);
              setFilters({
                anio: String(data?.meta.selected_year ?? ""),
                search: "",
              });
            }}
            actions={(
              <>
                {tab === "formacion" ? (
                  <button
                    type="button"
                    className="calidad-primary-button"
                    onClick={() => setYearForm({
                      open: true,
                      year: data?.meta.suggested_new_year ?? 2026,
                    })}
                  >
                    <CalendarPlus size={16} />
                    Nuevo año
                  </button>
                ) : (
                  <button
                    type="button"
                    className="calidad-primary-button"
                    onClick={() => setEvaluationYearForm({
                      open: true,
                      source: data?.meta.selected_year ?? data?.meta.years[data.meta.years.length - 1] ?? 2025,
                      target: data?.meta.suggested_new_year ?? 2026,
                      fields: evaluationCopyOptions.map((item) => item.id),
                    })}
                  >
                    <Copy size={16} />
                    Crear plantilla anual
                  </button>
                )}
                <button
                  type="button"
                  className="iso9001-filter-danger-button"
                  disabled={!data?.meta.selected_year}
                  onClick={() => data?.meta.selected_year && setDeleteTarget({
                    kind: "year",
                    id: data.meta.selected_year,
                    label: String(data.meta.selected_year),
                  })}
                >
                  <Trash2 size={16} />
                  Eliminar año
                </button>
              </>
            )}
          >
            <Iso900114001Select
              label="Año"
              value={filters.anio}
              onChange={(event) => setFilters({ ...filters, anio: event.target.value })}
            >
              {(data?.meta.years ?? []).map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </Iso900114001Select>
            <Iso900114001Select
              label="Empleado"
              value={selectedEmployeeId ?? ""}
              onChange={(event) => selectEmployee(
                event.target.value === "" ? null : Number(event.target.value),
              )}
            >
              <option value="">Todos los empleados</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.iniciales ? `${employee.iniciales} · ` : ""}{employee.nombre}
                </option>
              ))}
            </Iso900114001Select>
            <Iso900114001Input
              label="Búsqueda"
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
              placeholder="Curso, empleado, tema o asistentes"
            />
          </Iso900114001Filters>

          {tab === "formacion" ? (
            <Iso900114001MasterDetailTable
              eyebrow="Formación por empleado"
              title="Cursos y empleados"
              countLabel={`${visibleCourses.length} cursos visibles`}
              createLabel="Nuevo empleado"
              items={[
                { id: "all", label: "Todos los empleados", count: courses.length },
                ...employees.map((employee) => ({
                  id: employee.id,
                  label: `${employee.iniciales ? `${employee.iniciales} · ` : ""}${employee.nombre}`,
                  shortLabel: employee.iniciales || employee.nombre.slice(0, 2).toUpperCase(),
                  title: employee.nombre,
                  count: courseCountByEmployee.get(employee.id) ?? 0,
                })),
              ]}
              selectedId={selectedEmployeeId ?? "all"}
              loading={loading}
              emptyText="No hay empleados registrados."
              onCreate={() => openEmployee()}
              onSelect={(id) => selectEmployee(id === "all" ? null : Number(id))}
            >
              <div className="iso9001-detail-heading">
                <div>
                  <div className="iso9001-section-eyebrow">
                    {selectedEmployee ? "Empleado" : "Vista global"}
                  </div>
                  <h3>{selectedEmployee?.nombre ?? "Todos los empleados"}</h3>
                  <p>
                    {selectedEmployee
                      ? `${selectedEmployee.iniciales || "Sin iniciales"} · ${selectedEmployee.empresa || "Sin empresa"}`
                      : `${employees.length} empleados · ${visibleCourses.length} cursos visibles`}
                  </p>
                </div>
                {selectedEmployee ? (
                  <div className="calidad-table-actions-row">
                    <Iso900114001IconButton
                      label="Editar empleado"
                      icon={<Edit3 size={15} />}
                      onClick={() => openEmployee(selectedEmployee)}
                    />
                    <Iso900114001IconButton
                      label="Eliminar empleado"
                      icon={<Trash2 size={15} />}
                      danger
                      onClick={() => setDeleteTarget({
                        kind: "employee",
                        id: selectedEmployee.id,
                        label: selectedEmployee.nombre,
                      })}
                    />
                  </div>
                ) : null}
              </div>

              {selectedEmployee ? (
                <div className="iso9001-detail-meta">
                  <span>Antigüedad: <strong>{selectedEmployee.antiguedad_referencia || "—"}</strong></span>
                  <span>Formación SI: <strong>{selectedEmployee.formacion_si_referencia || "—"}</strong></span>
                  <span>Encuesta de emergencias: <strong>{selectedEmployee.encuesta_emergencias_referencia || "—"}</strong></span>
                  <span>PRL: <strong>{selectedEmployee.prl_referencia || "—"}</strong></span>
                </div>
              ) : null}

              <div className="iso9001-subtable-heading">
                <div>
                  <h4>
                    {selectedEmployee
                      ? `Cursos de ${selectedEmployee.nombre}`
                      : "Cursos de todos los empleados"}
                  </h4>
                  {!selectedEmployee ? <small>Selecciona un empleado para añadir un curso.</small> : null}
                </div>
                <button
                  type="button"
                  className="calidad-primary-button"
                  disabled={!data?.meta.selected_year_id || !selectedEmployee}
                  onClick={() => openCourse()}
                >
                  <Plus size={15} />
                  Añadir curso
                </button>
              </div>

              <div className="iso9001-crud-scroll">
                <table className="calidad-table iso9001-crud-table">
                  <thead>
                    <tr>
                      <th className="iso9001-actions-column">Acciones</th>
                      {!selectedEmployee ? <th>Empleado</th> : null}
                      <th>Fecha</th>
                      <th>Nombre</th>
                      <th>Categoría</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCourses.length === 0 ? (
                      <tr>
                        <td colSpan={selectedEmployee ? 4 : 5} className="iso9001-empty-table-cell">
                          No hay cursos para la selección actual.
                        </td>
                      </tr>
                    ) : visibleCourses.map((course) => (
                      <tr key={course.id}>
                        <td className="iso9001-actions-column">
                          <div className="calidad-table-actions-row">
                            <Iso900114001IconButton
                              label="Editar curso"
                              icon={<Edit3 size={14} />}
                              onClick={() => openCourse(course)}
                            />
                            <Iso900114001IconButton
                              label="Eliminar curso"
                              icon={<Trash2 size={14} />}
                              danger
                              onClick={() => setDeleteTarget({ kind: "course", id: course.id, label: course.nombre })}
                            />
                          </div>
                        </td>
                        {!selectedEmployee ? (
                          <td className="iso9001-long-column">
                            <Iso900114001ScrollableCell title={course.empleado?.nombre}>
                              {course.empleado?.nombre || "—"}
                            </Iso900114001ScrollableCell>
                          </td>
                        ) : null}
                        <td>{course.fecha || course.fecha_referencia || "—"}</td>
                        <td className="iso9001-extra-long-column">
                          <Iso900114001ScrollableCell title={course.nombre}>
                            {course.nombre}
                          </Iso900114001ScrollableCell>
                        </td>
                        <td className="iso9001-long-column">
                          <Iso900114001ScrollableCell title={course.categoria ?? undefined}>
                            {course.categoria || "—"}
                          </Iso900114001ScrollableCell>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Iso900114001MasterDetailTable>
          ) : (
            <Iso900114001CrudTable
              title={`Evaluación de la formación ${data?.meta.selected_year ?? ""}`}
              rows={evaluations}
              columns={evaluationColumns}
              getRowKey={(row) => row.id}
              loading={loading}
              error={error}
              createLabel="Añadir evaluación"
              emptyTitle="No hay evaluaciones para este año"
              onCreate={() => openEvaluation()}
              onEdit={openEvaluation}
              onDelete={(row) => setDeleteTarget({
                kind: "evaluation",
                id: row.id,
                label: row.tema,
              })}
              onRetry={() => void load(true)}
            />
          )}
        </>
      )}

      <Iso900114001Modal
        open={employeeForm.open}
        title={employeeForm.id ? "Editar empleado" : "Nuevo empleado"}
        wide
        submitting={submitting}
        error={formError}
        onClose={() => setEmployeeForm((current) => ({ ...current, open: false }))}
        onSubmit={() => void saveEmployeeForm()}
        footer={
          <>
            <button
              type="button"
              className="calidad-back-button"
              onClick={() => setEmployeeForm((current) => ({ ...current, open: false }))}
            >
              Cancelar
            </button>
            <button type="submit" className="calidad-primary-button" disabled={submitting}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Guardar
            </button>
          </>
        }
      >
        <Iso900114001Input
          label="Nombre"
          required
          value={employeeForm.value.nombre}
          onChange={(event) => setEmployeeForm({
            ...employeeForm,
            value: { ...employeeForm.value, nombre: event.target.value },
          })}
        />
        <Iso900114001Input
          label="Iniciales"
          value={employeeForm.value.iniciales ?? ""}
          onChange={(event) => setEmployeeForm({
            ...employeeForm,
            value: { ...employeeForm.value, iniciales: event.target.value },
          })}
        />
        <Iso900114001Input
          label="Empresa"
          value={employeeForm.value.empresa ?? ""}
          onChange={(event) => setEmployeeForm({
            ...employeeForm,
            value: { ...employeeForm.value, empresa: event.target.value },
          })}
        />
        <Iso900114001Input
          label="Antigüedad"
          value={employeeForm.value.antiguedad_referencia ?? ""}
          onChange={(event) => setEmployeeForm({
            ...employeeForm,
            value: { ...employeeForm.value, antiguedad_referencia: event.target.value },
          })}
        />
        <Iso900114001Input
          label="Formación del sistema"
          value={employeeForm.value.formacion_si_referencia ?? ""}
          onChange={(event) => setEmployeeForm({
            ...employeeForm,
            value: { ...employeeForm.value, formacion_si_referencia: event.target.value },
          })}
        />
        <Iso900114001Input
          label="Encuesta de emergencias"
          value={employeeForm.value.encuesta_emergencias_referencia ?? ""}
          onChange={(event) => setEmployeeForm({
            ...employeeForm,
            value: { ...employeeForm.value, encuesta_emergencias_referencia: event.target.value },
          })}
        />
        <Iso900114001Input
          label="PRL"
          value={employeeForm.value.prl_referencia ?? ""}
          onChange={(event) => setEmployeeForm({
            ...employeeForm,
            value: { ...employeeForm.value, prl_referencia: event.target.value },
          })}
        />
      </Iso900114001Modal>

      <Iso900114001Modal
        open={courseForm.open}
        title={courseForm.id ? "Editar curso" : "Nuevo curso"}
        submitting={submitting}
        error={formError}
        onClose={() => setCourseForm((current) => ({ ...current, open: false }))}
        onSubmit={() => void saveCourseForm()}
        footer={
          <>
            <button
              type="button"
              className="calidad-back-button"
              onClick={() => setCourseForm((current) => ({ ...current, open: false }))}
            >
              Cancelar
            </button>
            <button type="submit" className="calidad-primary-button" disabled={submitting}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Guardar
            </button>
          </>
        }
      >
        <Iso900114001Select
          label="Empleado"
          value={courseForm.value.empleado_id}
          onChange={(event) => setCourseForm({
            ...courseForm,
            value: { ...courseForm.value, empleado_id: Number(event.target.value) },
          })}
        >
          {employees.map((employee) => (
            <option key={employee.id} value={employee.id}>{employee.nombre}</option>
          ))}
        </Iso900114001Select>
        <Iso900114001Input
          label="Fecha"
          type="date"
          value={courseForm.value.fecha ?? ""}
          onChange={(event) => setCourseForm({
            ...courseForm,
            value: { ...courseForm.value, fecha: event.target.value || null },
          })}
        />
        <Iso900114001Input
          label="Fecha de referencia"
          value={courseForm.value.fecha_referencia ?? ""}
          onChange={(event) => setCourseForm({
            ...courseForm,
            value: { ...courseForm.value, fecha_referencia: event.target.value || null },
          })}
          placeholder="Ej. mayo25"
        />
        <Iso900114001Textarea
          label="Nombre del curso"
          fullWidth
          required
          rows={4}
          value={courseForm.value.nombre}
          onChange={(event) => setCourseForm({
            ...courseForm,
            value: { ...courseForm.value, nombre: event.target.value },
          })}
        />
        <Iso900114001Input
          label="Categoría"
          value={courseForm.value.categoria ?? ""}
          onChange={(event) => setCourseForm({
            ...courseForm,
            value: { ...courseForm.value, categoria: event.target.value || null },
          })}
        />
      </Iso900114001Modal>

      <Iso900114001Modal
        open={evaluationForm.open}
        title={evaluationForm.id ? "Editar evaluación" : "Nueva evaluación"}
        wide
        submitting={submitting}
        error={formError}
        onClose={() => setEvaluationForm((current) => ({ ...current, open: false }))}
        onSubmit={() => void saveEvaluationForm()}
        footer={
          <>
            <button
              type="button"
              className="calidad-back-button"
              onClick={() => setEvaluationForm((current) => ({ ...current, open: false }))}
            >
              Cancelar
            </button>
            <button type="submit" className="calidad-primary-button" disabled={submitting}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Guardar
            </button>
          </>
        }
      >
        <Iso900114001Input
          label="Tipo"
          value={evaluationForm.value.tipo ?? ""}
          onChange={(event) => setEvaluationForm({
            ...evaluationForm,
            value: { ...evaluationForm.value, tipo: event.target.value || null },
          })}
        />
        <Iso900114001Input
          label="Fecha"
          type="date"
          value={evaluationForm.value.fecha ?? ""}
          onChange={(event) => setEvaluationForm({
            ...evaluationForm,
            value: { ...evaluationForm.value, fecha: event.target.value || null },
          })}
        />
        <Iso900114001Input
          label="Fecha de referencia"
          value={evaluationForm.value.fecha_referencia ?? ""}
          onChange={(event) => setEvaluationForm({
            ...evaluationForm,
            value: { ...evaluationForm.value, fecha_referencia: event.target.value || null },
          })}
        />
        <Iso900114001Textarea
          label="Tema"
          fullWidth
          required
          rows={3}
          value={evaluationForm.value.tema}
          onChange={(event) => setEvaluationForm({
            ...evaluationForm,
            value: { ...evaluationForm.value, tema: event.target.value },
          })}
        />
        <Iso900114001Input
          label="Impartido por"
          value={evaluationForm.value.impartido_por ?? ""}
          onChange={(event) => setEvaluationForm({
            ...evaluationForm,
            value: { ...evaluationForm.value, impartido_por: event.target.value || null },
          })}
        />
        <Iso900114001Textarea
          label="Asistentes"
          fullWidth
          rows={3}
          value={evaluationForm.value.asistentes ?? ""}
          onChange={(event) => setEvaluationForm({
            ...evaluationForm,
            value: { ...evaluationForm.value, asistentes: event.target.value || null },
          })}
        />
        <Iso900114001Input
          label="Evaluación de eficacia"
          value={evaluationForm.value.evaluacion_eficacia ?? ""}
          onChange={(event) => setEvaluationForm({
            ...evaluationForm,
            value: { ...evaluationForm.value, evaluacion_eficacia: event.target.value || null },
          })}
        />
        <Iso900114001Textarea
          label="Observaciones"
          fullWidth
          rows={4}
          value={evaluationForm.value.observaciones ?? ""}
          onChange={(event) => setEvaluationForm({
            ...evaluationForm,
            value: { ...evaluationForm.value, observaciones: event.target.value || null },
          })}
        />
      </Iso900114001Modal>

      <Iso900114001Modal
        open={yearForm.open}
        title="Nuevo año"
        submitting={submitting}
        error={formError}
        onClose={() => setYearForm((current) => ({ ...current, open: false }))}
        onSubmit={() => void saveYear()}
        footer={
          <>
            <button
              type="button"
              className="calidad-back-button"
              onClick={() => setYearForm((current) => ({ ...current, open: false }))}
            >
              Cancelar
            </button>
            <button type="submit" className="calidad-primary-button" disabled={submitting}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <CalendarPlus size={16} />}
              Crear año
            </button>
          </>
        }
      >
        <Iso900114001Input
          label="Año"
          type="number"
          min={2000}
          max={2100}
          value={yearForm.year}
          onChange={(event) => setYearForm({ ...yearForm, year: Number(event.target.value) })}
        />
        <div className="iso9001-field full">
          <span>Contenido</span>
          <p>Se creará un año vacío para registrar formación y evaluaciones nuevas.</p>
        </div>
      </Iso900114001Modal>

      <Iso900114001Modal
        open={evaluationYearForm.open}
        title="Crear plantilla anual"
        eyebrow="Evaluación"
        submitting={submitting}
        error={formError}
        onClose={() => setEvaluationYearForm((current) => ({ ...current, open: false }))}
        onSubmit={() => void saveEvaluationYear()}
        footer={
          <>
            <button
              type="button"
              className="calidad-back-button"
              onClick={() => setEvaluationYearForm((current) => ({ ...current, open: false }))}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="calidad-primary-button"
              disabled={submitting || evaluationYearForm.fields.length === 0}
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}
              Crear año
            </button>
          </>
        }
      >
        <Iso900114001Select
          label="Año origen"
          value={evaluationYearForm.source}
          onChange={(event) => setEvaluationYearForm({
            ...evaluationYearForm,
            source: Number(event.target.value),
          })}
        >
          {(data?.meta.years ?? []).map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Iso900114001Select>
        <Iso900114001Input
          label="Año destino"
          type="number"
          min={2000}
          max={2100}
          value={evaluationYearForm.target}
          onChange={(event) => setEvaluationYearForm({
            ...evaluationYearForm,
            target: Number(event.target.value),
          })}
        />
        <div className="iso9001-field full">
          <span>Campos que se copiarán</span>
          <div className="iso9001-copy-actions">
            <button
              type="button"
              className="calidad-back-button"
              onClick={() => setEvaluationYearForm({
                ...evaluationYearForm,
                fields: evaluationCopyOptions.map((item) => item.id),
              })}
            >
              Seleccionar todo
            </button>
            <button
              type="button"
              className="calidad-back-button"
              onClick={() => setEvaluationYearForm({
                ...evaluationYearForm,
                fields: ["tema"],
              })}
            >
              Quitar opcionales
            </button>
          </div>
          <div className="iso9001-checkbox-grid">
            {evaluationCopyOptions.map((option) => (
              <Iso900114001Checkbox
                key={option.id}
                label={option.required ? `${option.label} (obligatorio)` : option.label}
                checked={evaluationYearForm.fields.includes(option.id)}
                disabled={option.required}
                onChange={(checked) => setEvaluationYearForm({
                  ...evaluationYearForm,
                  fields: checked
                    ? [...evaluationYearForm.fields, option.id]
                    : evaluationYearForm.fields.filter((field) => field !== option.id),
                })}
              />
            ))}
          </div>
          <p>La fecha real no se copiará. Las evaluaciones nuevas quedarán asociadas al año destino.</p>
        </div>
      </Iso900114001Modal>

      <Iso900114001ConfirmDialog
        open={deleteTarget !== null}
        title={deleteTarget?.kind === "year"
          ? `Eliminar el año ${deleteTarget.label}`
          : "Eliminar registro"}
        description={deleteTarget?.kind === "year"
          ? "Se eliminarán todos los cursos y evaluaciones asociados al año."
          : deleteTarget?.kind === "employee"
            ? `Se eliminará ${deleteTarget.label} y sus cursos asociados.`
            : `Se eliminará ${deleteTarget?.label ?? "el registro"}.`}
        confirming={submitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void remove()}
      />
    </section>
  );
}
